import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2 } from "lucide-react";
import { api, assetUrl, fmtDuration } from "@/lib/api";
import { PublicError } from "@/components/PublicShell";
import { CountryBreakdown } from "@/components/CountryBreakdown";
import { HubEngagement } from "@/components/HubEngagement";

const TIER = { hot: "bg-rose-100 text-rose-700", warm: "bg-amber-100 text-amber-700", cold: "bg-slate-100 text-slate-600" };
const Kpi = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-slate-200 p-5" data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <p className="text-3xl font-bold mt-2">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </div>
);

export default function ClientReport() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/public/client-reports/${token}`).then((r) => { setD(r.data); document.title = `${r.data.program.code} – Campaign performance report`; }).catch(() => setErr(true));
  }, [token]);

  const t = useMemo(() => {
    if (!d) return null;
    const s = d.pages.reduce((a, p) => ({ visits: a.visits + p.visits, uniq: a.uniq + p.unique_visitors, leads: a.leads + p.leads, time: a.time + p.total_watch_seconds, viewers: a.viewers + p.viewers, hot: a.hot + p.hot, warm: a.warm + p.warm, cold: a.cold + p.cold }),
      { visits: 0, uniq: 0, leads: 0, time: 0, viewers: 0, hot: 0, warm: 0, cold: 0 });
    return { ...s, conv: s.uniq ? Math.min(100, Math.round((s.leads / s.uniq) * 1000) / 10) : 0, avg: s.viewers ? s.time / s.viewers : 0 };
  }, [d]);

  if (err) return <PublicError text="This report link is invalid or has been reset." />;
  if (!d) return <div className="min-h-screen grid place-items-center bg-slate-100"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
  const p = d.program;
  const accent = p.hub?.accent || "#EC4899";
  const total = t.hot + t.warm + t.cold || 1;

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4 print:bg-white print:p-0" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className="max-w-5xl mx-auto mb-5 flex justify-end print:hidden">
        <button data-testid="client-report-print-button" onClick={() => window.print()} className="h-10 px-5 rounded-full text-white text-sm font-medium flex items-center gap-2" style={{ background: accent }}>
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>
      <div data-testid="client-report-page" className="max-w-5xl mx-auto bg-white text-slate-900 rounded-xl shadow-xl print:shadow-none print:rounded-none overflow-hidden">
        <div className="h-2" style={{ background: accent }} />
        <div className="px-10 py-10">
          <div className="flex items-start justify-between gap-6 mb-10">
            <div>
              {p.hub?.logo_path && <img src={assetUrl(p.hub.logo_path)} alt="Logo" className="h-10 w-auto object-contain mb-6" data-testid="client-report-logo" />}
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Campaign performance report</p>
              <h1 data-testid="client-report-title" className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">{p.name}</h1>
              <p className="text-sm text-slate-500 mt-2">{p.client_name}{p.client_website_url ? ` · ${p.client_website_url}` : ""}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Campaign ID</p>
              <p data-testid="client-report-code" className="font-mono font-semibold mt-1" style={{ color: accent }}>{p.code}</p>
              <p className="text-xs text-slate-500 mt-3">Generated {new Date(d.generated_at).toLocaleString()}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <Kpi testid="client-report-visits" label="Visits" value={t.visits} sub={`${t.uniq} unique visitors`} />
            <Kpi testid="client-report-leads" label="Leads" value={t.leads} sub={`${t.conv}% conversion`} />
            <Kpi testid="client-report-time" label="Avg engagement" value={fmtDuration(t.avg)} sub="reading or watching" />
            <Kpi testid="client-report-hot" label="Hot leads" value={t.hot} sub="engagement score 70+" />
          </div>

          <div className="mb-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Lead quality</p>
            <div className="flex h-3 rounded-full overflow-hidden bg-slate-100" data-testid="client-report-tier-bar">
              <div className="bg-rose-500" style={{ width: `${(t.hot / total) * 100}%` }} />
              <div className="bg-amber-400" style={{ width: `${(t.warm / total) * 100}%` }} />
              <div className="bg-slate-300" style={{ width: `${(t.cold / total) * 100}%` }} />
            </div>
            <p className="text-xs text-slate-500 mt-2">Hot {t.hot} · Warm {t.warm} · Cold {t.cold}</p>
          </div>

          <h2 className="text-lg font-bold mb-3">Content performance</h2>
          <table className="w-full text-sm mb-10" data-testid="client-report-pages">
            <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 font-medium">Content</th><th className="py-2 font-medium">Type</th><th className="py-2 font-medium text-right">Visits</th><th className="py-2 font-medium text-right">Leads</th><th className="py-2 font-medium text-right">Conv.</th><th className="py-2 font-medium text-right">Avg time</th><th className="py-2 font-medium text-right">Avg score</th>
            </tr></thead>
            <tbody>{d.pages.map((r) => (
              <tr key={r.page_id} className="border-b border-slate-100">
                <td className="py-2.5 font-medium">{r.page_name}</td><td className="py-2.5 text-slate-500">{r.campaign_type === "whitepaper" ? "White paper" : "Webinar"}</td>
                <td className="py-2.5 text-right font-mono">{r.visits}</td><td className="py-2.5 text-right font-mono">{r.leads}</td><td className="py-2.5 text-right font-mono">{r.conversion_rate == null ? "—" : `${r.conversion_rate}%`}</td>
                <td className="py-2.5 text-right font-mono">{fmtDuration(r.avg_watch_seconds)}</td><td className="py-2.5 text-right font-mono">{r.avg_score}</td>
              </tr>))}</tbody>
          </table>

          <div className="mb-10 break-inside-avoid"><CountryBreakdown data={d.countries} light testid="client-report-countries" /></div>

          {d.engagement && <HubEngagement data={d.engagement} light testid="client-report-engagement" />}

          <h2 className="text-lg font-bold mb-3">Leads by engagement</h2>
          <table className="w-full text-sm" data-testid="client-report-leads">
            <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 font-medium">Email</th><th className="py-2 font-medium">Country</th><th className="py-2 font-medium">Content</th><th className="py-2 font-medium text-right">Time</th><th className="py-2 font-medium text-right">Questions</th><th className="py-2 font-medium text-right">Score</th>
            </tr></thead>
            <tbody>{d.leads.map((l) => (
              <tr key={`${l.page_id}-${l.lead_id}`} className="border-b border-slate-100 break-inside-avoid">
                <td className="py-2">{l.email}</td><td className="py-2 text-slate-500">{l.country || "—"}</td><td className="py-2 text-slate-500">{l.page_name}</td>
                <td className="py-2 text-right font-mono">{fmtDuration(l.time_seconds)}</td><td className="py-2 text-right font-mono">{l.chat_count}</td>
                <td className="py-2 text-right"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIER[l.tier]}`}>{l.score} {l.tier}</span></td>
              </tr>))}
              {d.leads.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-slate-400">No leads yet.</td></tr>}
            </tbody>
          </table>
          <p className="text-[11px] text-slate-400 mt-10">Engagement score (0–100): time spent 40% · progress through content 30% · download / call-to-action 15% · questions asked 15%.</p>
        </div>
      </div>
    </div>
  );
}
